import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Modal } from "../components/ui/Modal/Modal";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { formatQuantity, formatDate, formatAmount } from "../lib/date";
import {
	useStockItemList,
	useStockItemDetail,
	useStockItemMovements,
	useStockItemSerials,
	useStockItemOpenOrders,
	useStockItemIncoming,
	useStockItemOpenPurchases,
	type StockItemListRow,
} from "../hooks/use-stock-items";
import { useWarehouseLabel } from "../hooks/use-warehouses";
import { useAuth } from "../lib/use-auth";
import { isStockCategory, STOCK_CATEGORY_LABELS } from "../lib/stock-categories";
import { StockCategoryView } from "../components/StockCategoryView/StockCategoryView";
import styles from "./StockItems.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/stock-items",
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
		status: String(search.status || ""),
		category: String(search.category || ""),
		brand: String(search.brand || ""),
	}),
	component: withPermission(StockItemsPage, "stock_items", "read"),
});

function StockItemsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuth();
	const isCustomer = user?.role === "customer";
	const { page, size, search, createdAtFrom, createdAtTo, status, category, brand } =
		route.useSearch();
	const showCategoryView = isStockCategory(category);
	const pageSize = size;
	const [stockModalItem, setStockModalItem] = useState<{ id: string; name: string } | null>(null);
	const [movementsModalItem, setMovementsModalItem] = useState<{ id: string; name: string } | null>(
		null,
	);
	const [serialsModalItem, setSerialsModalItem] = useState<{ id: string; name: string } | null>(
		null,
	);
	const [openOrdersModalItem, setOpenOrdersModalItem] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [incomingModalItem, setIncomingModalItem] = useState<{ id: string; name: string } | null>(
		null,
	);
	const [openPurchasesModalItem, setOpenPurchasesModalItem] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const { data, isLoading, isError, refetch } = useStockItemList({
		search: search || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		status: status || undefined,
		category: category || undefined,
		page,
		limit: pageSize,
		enabled: !showCategoryView,
	});

	const items = data?.items ?? [];
	const total = data?.total ?? 0;

	return (
		<div className={styles.page}>
			<PageHeader
				title={
					isStockCategory(category)
						? `${t("stockItems")} - ${t(STOCK_CATEGORY_LABELS[category])}`
						: t("stockItems")
				}
			/>

			{showCategoryView ? (
				<Card className={styles.cardFill ?? ""} bodyClassName={styles.cardBodyFill ?? ""}>
					<StockCategoryView
						key={category}
						category={category}
						brand={brand}
						onBrandChange={(b) =>
							navigate({ to: ".", search: (prev) => ({ ...prev, brand: b }) })
						}
						onOpenOrders={setOpenOrdersModalItem}
						onIncoming={setIncomingModalItem}
						onOpenPurchases={setOpenPurchasesModalItem}
					/>
				</Card>
			) : (
				<>
					<div className={styles.filters}>
						<FilterBar
							filters={[
								{ key: "search", type: "text", label: t("search") },
								{ key: "createdAt", type: "dateRange", label: t("createdAt") },
								...(isCustomer
									? []
									: [
											{
												key: "status",
												type: "select" as const,
												label: t("all"),
												options: [
													{ value: "active", label: t("active") },
													{ value: "inactive", label: t("inactive") },
												],
											},
										]),
							]}
							values={{ search, createdAtFrom, createdAtTo, status }}
							onChange={(vals) => {
								navigate({
									to: ".",
									search: {
										search: vals.search || "",
										createdAtFrom: vals.createdAtFrom || "",
										createdAtTo: vals.createdAtTo || "",
										status: vals.status || "",
										category,
										page: 1,
										size,
									},
								});
							}}
						/>
					</div>

					<Card className={styles.cardFill ?? ""} bodyClassName={styles.cardBodyFill ?? ""}>
						{isLoading && <LoadingSpinner />}
						{isError && <ErrorFallback onRetry={refetch} />}
						{!isLoading && !isError && (
							<Table
								className={styles.tableScroll}
								columns={[
									{ key: "code", header: t("code") },
									{ key: "name", header: t("name") },
									{ key: "category", header: t("category") },
									{ key: "uom", header: t("uom") },
									{
										key: "isSalesItem",
										header: t("salesItem"),
										render: (row) => (row.isSalesItem ? t("yes") : t("no")),
									},
									{
										key: "isActive",
										header: t("status"),
										render: (row) => (
											<StatusBadge status={row.isActive ? "active" : "inactive"} type="stockItem" />
										),
									},
									{
										key: "stock",
										header: t("stock"),
										render: (row) => (
											row.id ? (
												<button
													type="button"
													className={styles.stockButton}
													onClick={() => setStockModalItem({ id: row.id as string, name: row.name })}
												>
													{formatQuantity(row.totalStock)}
												</button>
											) : (
												<span>{formatQuantity(row.totalStock)}</span>
											)
										),
									},
									{
										key: "totalCommitted",
										header: t("reservedAmount"),
										render: (row: StockItemListRow) => (
											row.id ? (
												<button
													type="button"
													className={styles.stockButton}
													onClick={() => setOpenOrdersModalItem({ id: row.id as string, name: row.name })}
												>
													{formatQuantity(row.totalCommitted)}
												</button>
											) : (
												<span>{formatQuantity(row.totalCommitted)}</span>
											)
										),
									},
									{
										key: "remaining",
										header: t("remaining"),
										render: (row: StockItemListRow) => {
											const remaining =
												parseFloat(row.totalStock ?? "0") - parseFloat(row.totalCommitted ?? "0");
											return (
												<span className={remaining < 0 ? styles.shortage : undefined}>
													{formatQuantity(remaining)}
												</span>
											);
										},
									},
									{
										key: "totalIncoming",
										header: t("stockIncoming"),
										render: (row: StockItemListRow) => (
											row.id ? (
												<button
													type="button"
													className={styles.stockButton}
													onClick={() => setIncomingModalItem({ id: row.id as string, name: row.name })}
												>
													{formatQuantity(row.totalIncoming)}
												</button>
											) : (
												<span>{formatQuantity(row.totalIncoming)}</span>
											)
										),
									},
									// Stok kaydı (hareket) listesi müşteriye kapalı.
									...(isCustomer
										? []
										: [
												{
													key: "stockRecordList",
													header: t("stockRecordList"),
													render: (row: StockItemListRow) => (
														row.id ? (
															<button
																type="button"
																className={styles.stockButton}
																onClick={() => setMovementsModalItem({ id: row.id as string, name: row.name })}
															>
																{t("list")}
															</button>
														) : (
															null
														)
													),
												},
											]),
									{
										key: "serialList",
										header: t("serialList"),
										render: (row) =>
											row.manageSerialNumbers ? (
												row.id ? (
													<button
														type="button"
														className={styles.stockButton}
														onClick={() => setSerialsModalItem({ id: row.id as string, name: row.name })}
													>
														{t("showSerials")}
													</button>
												) : (
													null
												)
											) : null,
									},
									{
										key: "actions",
										header: t("actions"),
										render: (row: StockItemListRow) =>
											row.id ? (
												<Link to={`/stock-items/${row.id}` as never} className={styles.link}>
													{t("viewDetails")}
												</Link>
											) : null,
									},
								]}
								data={items}
								keyExtractor={(row) => row.id ?? row.code}
							/>
						)}
						<Pagination
							page={page}
							pageSize={pageSize}
							total={total}
							onPageChange={(p) => navigate({ to: ".", search: (prev) => ({ ...prev, page: p }) })}
							onPageSizeChange={(s) => navigate({ to: ".", search: (prev) => ({ ...prev, size: s, page: 1 }) })}
						/>
					</Card>
				</>
			)}

			{stockModalItem && (
				<StockModal
					item={stockModalItem}
					showDetails
					onClose={() => setStockModalItem(null)}
				/>
			)}
			{movementsModalItem && (
				<MovementsModal item={movementsModalItem} onClose={() => setMovementsModalItem(null)} />
			)}
			{incomingModalItem && (
				<IncomingModal item={incomingModalItem} onClose={() => setIncomingModalItem(null)} />
			)}
			{openPurchasesModalItem && (
				<OpenPurchasesModal
					item={openPurchasesModalItem}
					onClose={() => setOpenPurchasesModalItem(null)}
				/>
			)}
			{serialsModalItem && (
				<SerialsModal item={serialsModalItem} onClose={() => setSerialsModalItem(null)} />
			)}
			{openOrdersModalItem && (
				<OpenOrdersModal item={openOrdersModalItem} onClose={() => setOpenOrdersModalItem(null)} />
			)}
		</div>
	);
}

function StockModal({
	item,
	showDetails,
	onClose,
}: {
	item: { id: string; name: string };
	showDetails: boolean;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading } = useStockItemDetail(item.id);
	const warehouses = (data?.warehouseStocks ?? []).filter(
		(ws) => parseFloat(ws.currentStock ?? "0") > 0 || parseFloat(ws.reservedAmount ?? "0") > 0,
	);
	const totalStock = warehouses.reduce((sum, ws) => sum + parseFloat(ws.currentStock ?? "0"), 0);
	const stockedWarehouseCount = warehouses.filter(
		(ws) => parseFloat(ws.currentStock ?? "0") > 0,
	).length;

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("warehouseStock")}: ${item.name}`}
			maxWidth="min(95vw, 900px)"
		>
			{isLoading && <LoadingSpinner />}
			{!isLoading && warehouses.length === 0 && (
				<p className={styles.empty}>{t("noStockAvailable")}</p>
			)}
			{!isLoading && warehouses.length > 0 && (
				<>
					<p className={styles.totalStock}>
						{t("currentStock")}: <strong>{formatQuantity(totalStock)}</strong> ({stockedWarehouseCount}{" "}
						{t("warehouses")})
					</p>
					<table className={styles.stockTable}>
						<thead>
							<tr>
								<th>{t("code")}</th>
								<th>{t("name")}</th>
								<th>{t("currentStock")}</th>
								{showDetails && (
									<>
										<th>{t("reservedAmount")}</th>
										<th>{t("onOrderAmount")}</th>
										<th>{t("minStock")}</th>
										<th>{t("maxStock")}</th>
									</>
								)}
							</tr>
						</thead>
						<tbody>
							{warehouses.map((ws) => (
								<tr key={ws.id}>
									<td>{ws.warehouseCode || "-"}</td>
									<td>{ws.warehouseName || "-"}</td>
									<td>{formatQuantity(ws.currentStock)}</td>
									{showDetails && (
										<>
											<td>{formatQuantity(ws.reservedAmount)}</td>
											<td>{formatQuantity(ws.onOrderAmount)}</td>
											<td>{formatQuantity(ws.minStock)}</td>
											<td>{formatQuantity(ws.maxStock)}</td>
										</>
									)}
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}
		</Modal>
	);
}

function MovementsModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading } = useStockItemMovements(item.id);
	const movements = data?.movements ?? [];
	const warehouseLabel = useWarehouseLabel();

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("stockRecordList")}: ${item.name}`}
			maxWidth="min(95vw, 900px)"
		>
			{isLoading && <LoadingSpinner />}
			{!isLoading && movements.length === 0 && (
				<p className={styles.empty}>{t("noStockAvailable")}</p>
			)}
			{!isLoading && movements.length > 0 && (
				<table className={styles.stockTable}>
					<thead>
						<tr>
							<th>{t("date")}</th>
							<th>{t("document")}</th>
							<th>{t("warehouse")}</th>
							<th>{t("counterpart")}</th>
							<th>{t("quantity")}</th>
							<th>{t("price")}</th>
						</tr>
					</thead>
					<tbody>
						{movements.map((m, i) => (
							<tr key={i}>
								<td>{formatDate(m.date)}</td>
								<td>{m.document || "-"}</td>
								<td>{warehouseLabel(m.warehouseCode)}</td>
								<td>{m.counterpartName || "-"}</td>
								<td>{formatQuantity(m.quantity)}</td>
								<td>
									{m.price != null
										? `${formatAmount(m.price)}${m.currency ? m.currency : ""}`
										: "-"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</Modal>
	);
}

function OpenOrdersModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useStockItemOpenOrders(item.id);
	const orders = data?.orders ?? [];
	const warehouseLabel = useWarehouseLabel();
	const total = orders.reduce((sum, o) => sum + o.quantity, 0);

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("stockOpenSalesOrders")}: ${item.name}`}
			maxWidth="min(95vw, 900px)"
		>
			{isLoading && <LoadingSpinner />}
			{isError && <ErrorFallback message={t("sapConnectionError")} onRetry={refetch} />}
			{!isLoading && !isError && orders.length === 0 && (
				<p className={styles.empty}>{t("noData")}</p>
			)}
			{!isLoading && !isError && orders.length > 0 && (
				<>
					<p className={styles.totalStock}>
						{t("reservedAmount")}: <strong>{formatQuantity(total)}</strong>
					</p>
					<table className={styles.stockTable}>
						<thead>
							<tr>
								<th>{t("orderNumber")}</th>
								<th>{t("date")}</th>
								<th>{t("warehouse")}</th>
								<th>{t("stockViaSet")}</th>
								<th>{t("quantity")}</th>
							</tr>
						</thead>
						<tbody>
							{orders.map((o) => (
								<tr key={`${o.docEntry}-${o.viaItemCode ?? ""}-${o.warehouse ?? ""}`}>
									<td>{o.docNum}</td>
									<td>{formatDate(o.docDate)}</td>
									<td>{warehouseLabel(o.warehouse)}</td>
									<td>{o.viaItemCode ?? "-"}</td>
									<td>{formatQuantity(o.quantity)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}
		</Modal>
	);
}

function IncomingModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useStockItemIncoming(item.id);
	const arrivals = data?.arrivals ?? [];
	const total = arrivals.reduce((sum, a) => sum + parseFloat(a.quantity ?? "0"), 0);

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("stockIncoming")}: ${item.name}`}
			maxWidth="min(95vw, 900px)"
		>
			{isLoading && <LoadingSpinner />}
			{isError && <ErrorFallback onRetry={refetch} />}
			{!isLoading && !isError && arrivals.length === 0 && (
				<p className={styles.empty}>{t("noData")}</p>
			)}
			{!isLoading && !isError && arrivals.length > 0 && (
				<>
					<p className={styles.totalStock}>
						{t("stockIncoming")}: <strong>{formatQuantity(total)}</strong>
					</p>
					<table className={styles.stockTable}>
						<thead>
							<tr>
								<th>{t("reservationNo")}</th>
								<th>{t("vesselName")}</th>
								<th>{t("departureDate")}</th>
								<th>{t("estimatedArrivalDate")}</th>
								<th>{t("status")}</th>
								<th>{t("quantity")}</th>
							</tr>
						</thead>
						<tbody>
							{arrivals.map((a) => (
								<tr key={a.loadReportId}>
									<td>{a.reservationNo || "-"}</td>
									<td>{a.vesselName || "-"}</td>
									<td>{formatDate(a.departureDate)}</td>
									<td>{formatDate(a.estimatedArrivalDate)}</td>
									<td>{t(a.status)}</td>
									<td>{formatQuantity(a.quantity)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}
		</Modal>
	);
}

function OpenPurchasesModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useStockItemOpenPurchases(item.id);
	const orders = data?.orders ?? [];
	const total = orders.reduce((sum, o) => sum + o.unloadedQty, 0);

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("stockOpenPurchase")}: ${item.name}`}
			maxWidth="min(95vw, 900px)"
		>
			{isLoading && <LoadingSpinner />}
			{isError && <ErrorFallback message={t("sapConnectionError")} onRetry={refetch} />}
			{!isLoading && !isError && orders.length === 0 && (
				<p className={styles.empty}>{t("noData")}</p>
			)}
			{!isLoading && !isError && orders.length > 0 && (
				<>
					<p className={styles.totalStock}>
						{t("stockOpenPurchase")}: <strong>{formatQuantity(total)}</strong>
					</p>
					<table className={styles.stockTable}>
						<thead>
							<tr>
								<th>{t("orderNumber")}</th>
								<th>{t("date")}</th>
								<th>{t("stockDueDate")}</th>
								<th>{t("stockPurchaseOpenQty")}</th>
								<th>{t("stockLoadedQty")}</th>
								<th>{t("stockOpenPurchase")}</th>
							</tr>
						</thead>
						<tbody>
							{orders.map((o) => (
								<tr key={o.docEntry}>
									<td>{o.docNum}</td>
									<td>{formatDate(o.docDate)}</td>
									<td>{formatDate(o.dueDate)}</td>
									<td>{formatQuantity(o.openQty)}</td>
									<td>{formatQuantity(o.loadedQty)}</td>
									<td>{formatQuantity(o.unloadedQty)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}
		</Modal>
	);
}

export function SerialsModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading } = useStockItemSerials(item.id);
	const serials = data?.serials ?? [];

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("serialList")}: ${item.name}`}
			maxWidth="min(95vw, 700px)"
		>
			{isLoading && <LoadingSpinner />}
			{!isLoading && serials.length === 0 && (
				<p className={styles.empty}>{t("noStockAvailable")}</p>
			)}
			{!isLoading && serials.length > 0 && (
				<table className={styles.stockTable}>
					<thead>
						<tr>
							<th>{t("warehouse")}</th>
							<th>{t("serialNo")}</th>
						</tr>
					</thead>
					<tbody>
						{serials.map((s, i) => (
							<tr key={i}>
								<td>
									{s.warehouseName ? `${s.warehouseCode} - ${s.warehouseName}` : s.warehouseCode || "-"}
								</td>
								<td>{s.serialNo}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</Modal>
	);
}
